import { useCallback, type Dispatch, type MutableRefObject, type SetStateAction } from 'react';
import type { GridApi, IsFullWidthRowParams, RowHeightParams } from 'ag-grid-community';
import { getMasterDetailRowId } from '../masterDetail/rowModel';
import type { MasterDetailRowMarkers } from '../masterDetail/types';

/** Stable AG Grid adapters owned by MasterDetailGrid. */
export const useMasterDetailGridBindings = <TData extends object>({
  gridApiRef,
  setMasterGridApi,
  detailRowHeight,
  getDetailRowHeight,
  onGridReady,
}: {
  gridApiRef: MutableRefObject<GridApi | null>;
  setMasterGridApi: Dispatch<SetStateAction<GridApi | null>>;
  detailRowHeight: number;
  getDetailRowHeight?: (data: TData) => number;
  onGridReady?: (params: { api: GridApi }) => void;
}) => {
  const getRowIdFn = useCallback(
    (params: { data: MasterDetailRowMarkers }): string => getMasterDetailRowId(params.data),
    []
  );

  const isFullWidthRow = useCallback(
    (params: IsFullWidthRowParams<TData>) =>
      (params.rowNode.data as MasterDetailRowMarkers)?._isDetail === true,
    []
  );

  const getRowHeight = useCallback(
    (params: RowHeightParams<TData>) => {
      if ((params.data as MasterDetailRowMarkers)?._isDetail !== true) {
        return undefined;
      }
      if (getDetailRowHeight) {
        return getDetailRowHeight(params.data as TData);
      }
      return detailRowHeight;
    },
    [detailRowHeight, getDetailRowHeight]
  );

  const handleGridReady = useCallback(
    (params: { api: GridApi }) => {
      gridApiRef.current = params.api;
      setMasterGridApi(params.api);
      onGridReady?.(params);
    },
    [gridApiRef, onGridReady, setMasterGridApi]
  );

  const handleRowDataUpdated = useCallback((params: { api: GridApi }) => {
    params.api.resetRowHeights();
    params.api.redrawRows?.();
  }, []);

  return {
    getRowIdFn,
    isFullWidthRow,
    getRowHeight,
    handleGridReady,
    handleRowDataUpdated,
  };
};
